import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// Smart storefront-redirector.
// Läser aktivt läge + storefront-URL:er via public RPC `get_storefront_routing`.
// Om läget kräver att besökaren befinner sig på en annan storefront än den
// aktuella domänen → omdirigera. Skippar admin/auth/api-vägar och respekterar
// override-flagga i sessionStorage (för att undvika loopar och ge admins
// möjlighet att förhandsgranska den "fel" storefronten).

const SKIP_PATH_PREFIXES = ["/admin", "/auth", "/api", "/.well-known"];
const OVERRIDE_KEY = "storefront_redirect_override";
const OVERRIDE_QUERY = "storefront_override"; // ?storefront_override=off|lovable|shopify
const REDIRECT_TS_KEY = "storefront_redirect_ts";
const REDIRECT_LOOP_WINDOW_MS = 10_000;

type Mode = "lovable" | "shopify";

interface RoutingRow {
  mode: Mode;
  shopify_storefront_url: string | null;
  lovable_storefront_url: string | null;
  redirect_enabled: boolean;
}

const sameHost = (a: string, b: string): boolean => {
  try {
    return new URL(a).hostname.toLowerCase() === new URL(b).hostname.toLowerCase();
  } catch {
    return false;
  }
};

const StorefrontRedirector = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const path = location.pathname;
    if (SKIP_PATH_PREFIXES.some((p) => path === p || path.startsWith(p + "/"))) return;

    // Override via query — sticky för sessionen
    const params = new URLSearchParams(location.search);
    const override = params.get(OVERRIDE_QUERY);
    if (override) {
      sessionStorage.setItem(OVERRIDE_KEY, override);
    }
    const stickyOverride = sessionStorage.getItem(OVERRIDE_KEY);
    if (stickyOverride === "off") return;

    // Loop-skydd
    const lastRedirect = Number(sessionStorage.getItem(REDIRECT_TS_KEY) || 0);
    if (Date.now() - lastRedirect < REDIRECT_LOOP_WINDOW_MS) return;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_storefront_routing");
      if (cancelled || error || !data) return;
      const row = (Array.isArray(data) ? data[0] : data) as RoutingRow | undefined;
      if (!row || !row.redirect_enabled) return;

      const effectiveMode = (stickyOverride === "lovable" || stickyOverride === "shopify"
        ? stickyOverride
        : row.mode) as Mode;

      const target =
        effectiveMode === "shopify" ? row.shopify_storefront_url : row.lovable_storefront_url;
      if (!target) return;

      const currentOrigin = window.location.origin;
      if (sameHost(currentOrigin, target)) return; // redan på rätt storefront

      // För mode=lovable: bara redirecta om vi faktiskt sitter på Shopify-hosten
      // (annars är vi redan på en Lovable-domän — preview/lovable.app/zooi.se).
      if (effectiveMode === "lovable") {
        const shopifyUrl = row.shopify_storefront_url;
        if (!shopifyUrl || !sameHost(currentOrigin, shopifyUrl)) return;
      }

      const dest = new URL(target);
      dest.pathname = path;
      dest.search = location.search;
      dest.hash = location.hash;
      sessionStorage.setItem(REDIRECT_TS_KEY, String(Date.now()));
      window.location.replace(dest.toString());
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash]);

  return null;
};

export default StorefrontRedirector;
